import { Stack } from '@mui/material';
import {
  groupTypeMap,
  PeriodTypeEnum,
  useGetFacilitiesQuery,
  useGetGroupScheduleQuery,
  WeeklySchedule,
} from '@schooly/api';
import { Loading, ScheduleGrid, ScheduleGridTooltip, TooltipComponentProps } from '@schooly/style';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { AccessDenied } from '@schooly/style';
import { PermissionGuard } from '../../../../components/Guards';
import { useGroup } from '../../../../context/groups/useGroup';
import { useSchool } from '../../../../hooks/useSchool';
import { TIMETABLE_ACCESS_PERMISSIONS } from '../../../../hooks/useTimetablePermissions';
import {
  TransformScheduleOptions,
  useWeeklyScheduleGrid,
} from '../../../../hooks/useWeeklyScheduleGrid';
import {
  addScheduleEndAdornments,
  StructureSelect,
  TimeSlotTooltipContent,
} from '../../../ProfileModal/tabs/ProfileModalSchedule/ProfileModalSchedule';
import {
  buildRoomMap,
  ScheduleGroupWithRoom,
  ScheduleItemData,
} from '../../../ProfileModal/tabs/ProfileModalSchedule/utils';
import { NoTimetablesStub } from '../../../School/SchoolTimetables/SchoolTimetables';
import { groupSchedulesToWeeklySchedules } from './utils';

const GROUP_ALLOWED_PERIOD_TYPES = [PeriodTypeEnum.Lesson, PeriodTypeEnum.Registration];

const options: TransformScheduleOptions = {
  allowedPeriodTypes: GROUP_ALLOWED_PERIOD_TYPES,
};

export type GroupScheduleItemData = {
  groupWithRoom: ScheduleGroupWithRoom;
  onGroupClick?: (groupId: string) => void;
};

export const GroupPreviewModalTimetable: FC = () => {
  const { formatMessage } = useIntl();
  const { groupId, group } = useGroup();

  const { schoolId = '' } = useSchool();

  const { data, isLoading, isFetching } = useGetGroupScheduleQuery(
    { groupId: groupId ?? '' },
    { enabled: !!groupId, refetchOnMount: 'always' },
  );

  const { data: facilitiesData } = useGetFacilitiesQuery(
    { schoolId: schoolId ?? '' },
    { enabled: !!schoolId, refetchOnMount: 'always' },
  );

  const displayGroup = useMemo<WeeklySchedule['groups'][number] | undefined>(() => {
    if (!group || group.group_type === undefined) return;

    return {
      id: group.id,
      name: group.name,
      type: groupTypeMap[group.group_type],
      subject: group.subject
        ? {
            id: group.subject.id,
            name: group.subject.name,
            archived: group.subject.archived ?? false,
          }
        : null,
      staff_preview: group.staff.reduce<WeeklySchedule['groups'][number]['staff_preview']>(
        (acc, s) =>
          s.is_actual
            ? [
                ...acc,
                {
                  relation_id: s.user.relation_id,
                  user_id: s.user.user_id ?? '',
                  last_name: s.user.last_name,
                  given_name: s.user.given_name,
                  known_as: s.user.known_as ?? null,
                  title: s.user.title ?? null,
                },
              ]
            : acc,
        [],
      ),
    };
  }, [group]);

  const {
    structures,
    selectedStructure,
    setSelectedStructureId,
    items,
    days,
    timeRange,
    hasSchedule,
  } = useWeeklyScheduleGrid({
    schedule:
      data && displayGroup ? groupSchedulesToWeeklySchedules(data, displayGroup) : undefined,
    transformOptions: options,
    roomMap: facilitiesData ? buildRoomMap(facilitiesData.facilities) : new Map<string, string[]>(),
  });

  const itemsWithAdornments = useMemo(() => addScheduleEndAdornments(items), [items]);

  if (isLoading || isFetching) {
    return <Loading />;
  }

  return (
    <PermissionGuard permissions={TIMETABLE_ACCESS_PERMISSIONS} fallback={<AccessDenied />}>
      <Stack sx={{ height: '100%' }}>
        <StructureSelect
          structures={structures}
          selectedStructure={selectedStructure}
          onSelect={setSelectedStructureId}
        />
        {hasSchedule ? (
          <Stack sx={{ flex: 1, width: '100%', height: '100%', pt: 2.5 }}>
            <ScheduleGrid<ScheduleItemData>
              days={days}
              timeRange={timeRange}
              items={itemsWithAdornments}
              TooltipComponent={({ item, size, children }) => {
                const groupWithRoom = item.data.groupsWithRoom[0];
                if (!groupWithRoom) return <>{children}</>;

                return (
                  <GroupScheduleTooltip
                    item={{ ...item, data: { groupWithRoom } }}
                    size={size}
                    children={children}
                  />
                );
              }}
            />
          </Stack>
        ) : (
          <NoTimetablesStub label={formatMessage({ id: 'groups-Timetable-NoActiveTimetable' })} />
        )}
      </Stack>
    </PermissionGuard>
  );
};

const GroupScheduleTooltip: FC<TooltipComponentProps<GroupScheduleItemData>> = ({
  item,
  children,
}) => (
  <ScheduleGridTooltip
    content={
      <TimeSlotTooltipContent
        startTime={item.startTime}
        endTime={item.endTime}
        groupsWithRoom={[item.data.groupWithRoom]}
      />
    }
  >
    {children}
  </ScheduleGridTooltip>
);
